import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  refusalReason,
  shortDate,
  type ChannelConsent,
  type Contact,
  type ContactInput,
  type ContactRole,
  type Issue,
} from '../lib/api'
import { ContactChannels } from './ContactChannels'
import { ErrorBox, Loading } from './DataTable'
import { Status, Toggletip } from './kit'
import { ReasonTip } from './vendor/ReasonTip'
import { ErrorReferenceOf } from './ui'

// The order a campaign resolves in, not an alphabetical list. Only the first
// three are ranked against each other; below them the API takes whichever
// active contact has an address, so nothing here should suggest that billing
// outranks grievance.
const RANKED = 3

const ROLES: { role: ContactRole; label: string; detail: string }[] = [
  {
    role: 'PRIMARY',
    label: 'Primary',
    detail: 'Answers for the vendor day to day. A campaign tries this one first.',
  },
  {
    role: 'AUTHORISED_SIGNATORY',
    label: 'Authorised signatory',
    detail: 'Can commit the company to what the answers say. Tried when there is no primary.',
  },
  {
    role: 'SECURITY',
    label: 'Security',
    detail: 'Usually the person who actually knows the answer. Tried third.',
  },
  {
    role: 'PRIVACY',
    label: 'Privacy',
    detail: 'The data protection officer, or whoever holds that brief.',
  },
  {
    role: 'GRIEVANCE',
    label: 'Grievance officer',
    detail: 'The name a data principal is given. Not for questionnaires.',
  },
  {
    role: 'ESCALATION',
    label: 'Escalation',
    detail: 'For when the vendor stops replying, not for the first ask.',
  },
  {
    role: 'BILLING',
    label: 'Billing',
    detail: 'Part of the contract trail. Rarely the right person to ask about controls.',
  },
]

const ROLE_LABEL: Record<string, string> = Object.fromEntries(ROLES.map((r) => [r.role, r.label]))

// Both mirror packages/core/domain exactly. A refusal the server would make and
// the form would not is a round trip spent learning something already known.
const E164 = /^\+[1-9][0-9]{7,14}$/
const EMAIL = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/

type Draft = {
  role: string
  fullName: string
  designation: string
  email: string
  mobileE164: string
}

const EMPTY_DRAFT: Draft = { role: 'PRIMARY', fullName: '', designation: '', email: '', mobileE164: '' }

type Editing = { mode: 'new' } | { mode: 'edit'; id: string }

// Erasure needs an approver role, editing needs a write role, and the two are
// refused with the same status. Carrying the attempt is what keeps a 403 on
// Deactivate from telling an auditor they cannot erase.
type Attempt = 'save' | 'activate' | 'erase'

type Outcome = { id: string; ok?: string; error?: ApiError; attempt?: Attempt }

// The same test the API applies when it counts reachable. A mobile number is
// enough to sign a respondent in and not enough to invite one, so a contact
// with only a number is active and still unreachable.
function isReachable(c: Contact): boolean {
  return !c.erasedAt && c.isActive && (c.email ?? '').trim() !== ''
}

function validate(d: Draft): Issue[] {
  const issues: Issue[] = []
  const role = d.role.trim().toUpperCase()
  const name = d.fullName.trim()
  const email = d.email.trim()
  const mobile = d.mobileE164.trim()

  if (role === '') {
    issues.push({
      field: 'role',
      code: 'REQUIRED',
      detail: 'Name the role. A contact with no role is never chosen by a campaign.',
    })
  } else if (!ROLES.some((r) => r.role === role)) {
    issues.push({
      field: 'role',
      code: 'INVALID',
      detail: 'Not a role this system knows. The set is ' + ROLES.map((r) => r.role).join(', ') + '.',
    })
  }
  if (name === '') {
    issues.push({
      field: 'fullName',
      code: 'REQUIRED',
      detail: 'A contact is a person, and an attestation records who made it.',
    })
  }
  if (email === '' && mobile === '') {
    issues.push({
      field: 'email',
      code: 'REQUIRED',
      detail: 'Give an email or a mobile, or this contact cannot be invited or sent a code.',
    })
  }
  if (email !== '' && !EMAIL.test(email)) {
    issues.push({ field: 'email', code: 'INVALID', detail: 'That is not a usable email address.' })
  }
  if (mobile !== '' && !E164.test(mobile)) {
    issues.push({
      field: 'mobileE164',
      code: 'INVALID',
      detail: 'Use the country code and no spaces, for example +919876543210.',
    })
  }
  return issues
}

function issueFor(issues: Issue[], field: string): Issue | undefined {
  return issues.find((i) => i.field === field)
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

// A 403 here is not a failure, it is the rule working. Saying so is the
// difference between an operator asking the right person and an operator
// filing a bug against a screen that behaved correctly.
function Refusal({
  error,
  attempted,
  onDismiss,
}: {
  error: ApiError
  attempted: Attempt
  onDismiss: () => void
}): JSX.Element {
  let heading = 'That did not go through'
  let body = error.message
  let next = error.remedy

  if (error.status === 403 && attempted === 'erase') {
    heading = 'Your role cannot erase a contact'
    body = 'Erasure destroys personal data, so it needs a second pair of eyes.'
    next = refusalReason('privacy.erase')
  } else if (error.status === 403) {
    heading = 'Your role cannot change contacts'
    body = 'Reading a vendor and changing its contacts are separate permissions.'
    next = refusalReason('vendor.edit')
  } else if (error.status === 409) {
    heading = 'This contact has already been erased'
    next =
      attempted === 'erase'
        ? 'Reload the panel. The personal data is already gone.'
        : 'Reload the panel, then add a new contact. An erasure cannot be undone.'
  } else if (error.status === 422 && error.issues.length > 0) {
    heading = 'Refused'
    body = error.issues.map((i) => i.detail).join(' ')
  }

  return (
    <div className="note stop decision-note" role="alert">
      <strong>{heading}</strong>
      <p>{body}</p>
      <p className="muted">{next}</p>
      {/* The code and the reference support looks the request up by
          (release 3.1); shown here, so the feedback net adds no toast. */}
      <ErrorReferenceOf error={error} />
      <button type="button" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  )
}

export function VendorContacts({ vendorId }: { vendorId: string }): JSX.Element {
  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [note, setNote] = useState('')
  const [reachable, setReachable] = useState(0)
  const [error, setError] = useState<unknown>(null)

  const [editing, setEditing] = useState<Editing | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [issues, setIssues] = useState<Issue[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<ApiError | null>(null)

  const [busyId, setBusyId] = useState<string | null>(null)
  const [erasingId, setErasingId] = useState<string | null>(null)
  const [eraseReason, setEraseReason] = useState('')
  const [eraseIssue, setEraseIssue] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [consents, setConsents] = useState<ChannelConsent[]>([])

  const load = useCallback(() => {
    api
      .contacts(vendorId)
      .then((r) => {
        setContacts(r.contacts)
        setReachable(r.reachable)
        setNote(r.note)
        setError(null)
      })
      .catch(setError)
    // The channels are secondary to the contacts themselves: a failure to read
    // them leaves the list usable and the channels showing their defaults.
    api
      .vendorChannels(vendorId)
      .then((r) => setConsents(r.consents))
      .catch(() => setConsents([]))
  }, [vendorId])

  useEffect(load, [load])

  // Deliberately not re-sorted. The API returns these in the precedence order
  // it walks to set wouldBeInvited, so any ordering derived here could only
  // disagree with the answer it gave, on the one screen where the order is the
  // point.
  const ordered = contacts ?? []

  if (error != null) return <ErrorBox error={error} what="This vendor's contacts" />
  if (!contacts) return <Loading what="the contacts" />

  const canErase = can('privacy.erase')
  const canEdit = can('vendor.edit')
  const invited = ordered.find((c) => c.wouldBeInvited)
  const active = ordered.filter((c) => !c.erasedAt && c.isActive)
  const erasedCount = ordered.filter((c) => c.erasedAt).length

  function beginNew() {
    setEditing({ mode: 'new' })
    setDraft(EMPTY_DRAFT)
    setIssues([])
    setSaveError(null)
    setOutcome(null)
  }

  function beginEdit(c: Contact) {
    setEditing({ mode: 'edit', id: c.id })
    setDraft({
      role: c.role,
      fullName: c.fullName ?? '',
      designation: c.designation ?? '',
      email: c.email ?? '',
      mobileE164: c.mobileE164 ?? '',
    })
    setIssues([])
    setSaveError(null)
    setOutcome(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!editing) return
    const found = validate(draft)
    setIssues(found)
    if (found.length > 0) return

    const body: ContactInput = {
      role: draft.role,
      fullName: draft.fullName.trim(),
      designation: draft.designation.trim(),
      email: draft.email.trim(),
      mobileE164: draft.mobileE164.trim(),
    }
    setSaving(true)
    setSaveError(null)

    const call =
      editing.mode === 'new'
        ? api.createContact(vendorId, body).then((r) => r.note)
        : api
            .updateContact(editing.id, body)
            .then(() => 'Saved. The reference and everything answered under it are unchanged.')

    call
      .then((message) => {
        setEditing(null)
        setDraft(EMPTY_DRAFT)
        setOutcome({ id: 'form', ok: message })
        load()
      })
      .catch((err) => {
        const failed = asApiError(err)
        setSaveError(failed)
        if (failed.issues.length > 0) setIssues(failed.issues)
      })
      .finally(() => setSaving(false))
  }

  function toggleActive(c: Contact) {
    setBusyId(c.id)
    setOutcome(null)
    api
      .setContactActive(c.id, !c.isActive)
      .then((r) => {
        setOutcome({ id: c.id, ok: r.note })
        load()
      })
      .catch((err) => setOutcome({ id: c.id, error: asApiError(err), attempt: 'activate' }))
      .finally(() => setBusyId(null))
  }

  function erase(c: Contact, reason: string) {
    setBusyId(c.id)
    setOutcome(null)
    api
      .eraseContact(c.id, reason)
      .then((r) => {
        setErasingId(null)
        setEraseReason('')
        setOutcome({ id: c.id, ok: r.note })
        load()
      })
      .catch((err) => setOutcome({ id: c.id, error: asApiError(err), attempt: 'erase' }))
      .finally(() => setBusyId(null))
  }

  const roleIssue = issueFor(issues, 'role')
  const nameIssue = issueFor(issues, 'fullName')
  const emailIssue = issueFor(issues, 'email')
  const mobileIssue = issueFor(issues, 'mobileE164')
  const chosenRole = ROLES.find((r) => r.role === draft.role)
  const formOutcome = outcome !== null && outcome.id === 'form' ? outcome : null

  return (
    <div className="panel panel-flush">
      <div className="stat-strip">
        <div className={reachable === 0 ? 'stat alert' : 'stat'}>
          <b>{reachable}</b>
          <span>reachable by email</span>
        </div>
        <div className="stat">
          <b>{active.length}</b>
          <span>active contacts</span>
        </div>
        <div className="stat">
          <b>{erasedCount}</b>
          <span>erased</span>
        </div>
      </div>

      <div className="contact-block">
        {reachable === 0 ? (
          <div className="note stop" role="status">
            <strong>No questionnaire can be sent to this vendor</strong>
            <p className="vd-inline">
              {contacts.length === 0 ? 'Nobody is on record. Add a contact with an email first' : 'No active contact has an email. Add one first'}
              <Toggletip label="Why this matters">A campaign that includes this vendor would address nobody and say nothing about it.</Toggletip>
            </p>
          </div>
        ) : invited ? (
          <p className="summary-line vd-inline" role="status">
            <span className="vd-quiet">Next campaign goes to</span>
            <strong>{invited.fullName || 'this contact'}</strong>
            <span className="mono">{invited.email}</span>
            <Status tone="neutral">{ROLE_LABEL[invited.role] ?? invited.role}</Status>
          </p>
        ) : (
          <p className="summary-line vd-inline" role="status">
            <Status tone="warn">No usable address</Status>
            Check the addresses below before raising a campaign
          </p>
        )}
        <p className="vd-inline vd-quiet">
          How a campaign chooses
          <ReasonTip label="How a campaign chooses" text={note} />
        </p>
      </div>

      <div className="section-head">In the order a campaign tries them</div>

      <div className="contact-block">
        <ol className="vd-order">
          {ROLES.slice(0, RANKED).map((r) => (
            <li key={r.role}>
              <strong>{r.label}</strong>
              <Toggletip label={'About ' + r.label.toLowerCase()}>{r.detail}</Toggletip>
            </li>
          ))}
          <li>
            <span>Anyone else active with an address</span>
            <Toggletip label="After those three">
              Past those three no role outranks another, which is how a questionnaire reaches accounts when billing is the only one with an email.
            </Toggletip>
          </li>
        </ol>
      </div>

      {ordered.length === 0 ? (
        <div className="contact-block">
          <div className="empty">
            <strong>Nobody is on record for this vendor</strong>
            <span className="vd-inline">
              This vendor cannot be assessed until one exists
              <Toggletip label="Why a contact is needed">
                The vendor portal signs people in against these contacts, and invitations and reminders are sent to them.
              </Toggletip>
            </span>
          </div>
        </div>
      ) : (
        <ul className="exc-list">
          {ordered.map((c) => {
            const dead = Boolean(c.erasedAt)
            const rowOutcome = outcome !== null && outcome.id === c.id ? outcome : null
            const rowClass = dead ? 'exc contact-erased' : c.wouldBeInvited ? 'exc contact-invited' : 'exc'
            return (
              <li key={c.id} className={rowClass}>
                <div className="exc-head">
                  <h3 className="exc-title">
                    {dead ? <span className="muted">Erased contact</span> : c.fullName || 'Unnamed contact'}
                    {!dead && c.designation !== undefined && c.designation !== '' && (
                      <span className="muted">, {c.designation}</span>
                    )}
                  </h3>
                  <span className="chiprow">
                    <span className="pill">{ROLE_LABEL[c.role] ?? c.role}</span>
                    {dead ? (
                      <Status tone="plain">Erased</Status>
                    ) : c.isActive ? (
                      <Status tone="ok">Active</Status>
                    ) : (
                      <Status tone="neutral">Inactive</Status>
                    )}
                    {c.wouldBeInvited && <Status tone="info">Invited next</Status>}
                  </span>
                </div>

                <div className="exc-meta">
                  <span className="mono">{c.respondentRef}</span>
                  {!dead && c.email !== undefined && c.email !== '' && <span className="owner">{c.email}</span>}
                  {!dead && c.mobileE164 !== undefined && c.mobileE164 !== '' && (
                    <span className="mono">{c.mobileE164}</span>
                  )}
                  {c.retainUntil !== undefined && c.retainUntil !== '' && (
                    <span>retain until {shortDate(c.retainUntil)}</span>
                  )}
                </div>

                {!dead && (
                  <ContactChannels
                    vendorId={vendorId}
                    contact={c}
                    consents={consents.filter((k) => k.contactId === c.id)}
                    canEdit={canEdit}
                    onSaved={load}
                  />
                )}

                {dead ? (
                  <p className="field-note vd-inline">
                    Erased {shortDate(c.erasedAt)}. Reason: {c.erasureReason || 'none given'}
                    <Toggletip label="Why the reference stays">
                      The reference is kept, so an assessment this person answered still resolves and still reports that the respondent was erased.
                    </Toggletip>
                  </p>
                ) : (
                  !isReachable(c) && (
                    <p className="self-note vd-inline">
                      {c.isActive ? 'No email address, so a campaign cannot address this contact' : 'Inactive, so a campaign skips this contact'}
                      {c.isActive && (
                        <Toggletip label="About a mobile number">A mobile number signs a respondent in. It does not carry the invitation.</Toggletip>
                      )}
                    </p>
                  )
                )}

                {!dead && (
                  <div className="exc-actions">
                    <button
                      type="button"
                      onClick={() => beginEdit(c)}
                      disabled={!canEdit}
                      title={canEdit ? undefined : refusalReason('vendor.edit')}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busyId === c.id || !canEdit}
                      title={canEdit ? undefined : refusalReason('vendor.edit')}
                      onClick={() => toggleActive(c)}
                      data-action="tprm.vendor,vendor-record.a10"
                    >
                      {c.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={!canErase}
                      title={canErase ? undefined : refusalReason('privacy.erase')}
                      aria-expanded={erasingId === c.id}
                      aria-controls={'erase-' + c.id}
                      onClick={() => {
                        setErasingId(erasingId === c.id ? null : c.id)
                        setEraseReason('')
                        setEraseIssue(false)
                        setOutcome(null)
                      }}
                    >
                      Erase under the DPDP Act
                    </button>
                  </div>
                )}


                {!dead && !canErase && (
                  <p className="self-note vd-inline">
                    Erasing needs another role
                    <ReasonTip label="Who can erase" text={refusalReason('privacy.erase')} />
                  </p>
                )}

                {erasingId === c.id && !dead && (
                  <form
                    className="reject-form"
                    id={'erase-' + c.id}
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (!eraseReason.trim()) {
                        setEraseIssue(true)
                        return
                      }
                      setEraseIssue(false)
                      erase(c, eraseReason.trim())
                    }}
                  >
                    <label htmlFor={'erase-reason-' + c.id}>Why this contact is being erased</label>
                    <p className="hint vd-inline" id={'erase-help-' + c.id}>
                      Clears name, email, mobile and designation. Cannot be undone. Reference{' '}
                      <span className="mono">{c.respondentRef}</span> stays
                      <Toggletip label="Why the reference stays">So answers given under it still resolve. Write the request you are acting on.</Toggletip>
                    </p>
                    <div className="reject-row">
                      <input
                        id={'erase-reason-' + c.id}
                        type="text"
                        placeholder="The request you are acting on"
                        value={eraseReason}
                        aria-invalid={eraseIssue ? true : undefined}
                        aria-describedby={'erase-help-' + c.id + (eraseIssue ? ' erase-issue-' + c.id : '')}
                        onChange={(e) => {
                          setEraseReason(e.target.value)
                          if (e.target.value.trim()) setEraseIssue(false)
                        }}
                      />
                      <button
                        type="submit"
                        className="danger"
                        disabled={busyId === c.id}
                        data-action="tprm.vendor,vendor-record.a11"
                      >
                        {busyId === c.id ? 'Erasing' : 'Erase permanently'}
                      </button>
                      <button type="button" onClick={() => setErasingId(null)}>
                        Cancel
                      </button>
                    </div>
                    {eraseIssue && (
                      <p className="field-issue" id={'erase-issue-' + c.id} role="alert">
                        Record why. An erasure with no reason cannot be told apart from a mistake.
                      </p>
                    )}
                  </form>
                )}

                {rowOutcome !== null && rowOutcome.ok !== undefined && (
                  <p className="ok-note" role="status">
                    {rowOutcome.ok}
                  </p>
                )}
                {rowOutcome !== null && rowOutcome.error !== undefined && (
                  <Refusal
                    error={rowOutcome.error}
                    attempted={rowOutcome.attempt ?? 'save'}
                    onDismiss={() => setOutcome(null)}
                  />
                )}
              </li>
            )
          })}
        </ul>
      )}

      <div className="section-head">{editing !== null && editing.mode === 'edit' ? 'Edit contact' : 'Add a contact'}</div>

      <div className="contact-block">
        {formOutcome !== null && formOutcome.ok !== undefined && (
          <p className="ok-note" role="status">
            {formOutcome.ok}
          </p>
        )}

        {editing === null ? (
          <button
            type="button"
            className="primary"
            onClick={beginNew}
            disabled={!canEdit}
            title={canEdit ? undefined : refusalReason('vendor.edit')}
          >
            Add a contact
          </button>
        ) : (
          <form className="fieldset" onSubmit={submit} noValidate>
            {saveError !== null && saveError.status !== 422 && (
              <Refusal error={saveError} attempted="save" onDismiss={() => setSaveError(null)} />
            )}

            <div className="form-grid">
              <div className="field">
                <label htmlFor="vc-role">Role</label>
                <select
                  id="vc-role"
                  value={draft.role}
                  aria-invalid={roleIssue ? true : undefined}
                  aria-describedby={'vc-role-note' + (roleIssue ? ' vc-role-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                >
                  {ROLES.map((r) => (
                    <option key={r.role} value={r.role}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <p className="field-note" id="vc-role-note">
                  {chosenRole ? chosenRole.detail : 'Listed in the order a campaign tries them.'}
                </p>
                {roleIssue && (
                  <p className="field-error" id="vc-role-issue" role="alert">
                    {roleIssue.detail}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="vc-name">Full name</label>
                <input
                  id="vc-name"
                  type="text"
                  value={draft.fullName}
                  aria-invalid={nameIssue ? true : undefined}
                  aria-describedby={nameIssue ? 'vc-name-issue' : undefined}
                  onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
                />
                {nameIssue && (
                  <p className="field-error" id="vc-name-issue" role="alert">
                    {nameIssue.detail}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="vc-designation">Designation</label>
                <input
                  id="vc-designation"
                  type="text"
                  value={draft.designation}
                  aria-describedby="vc-designation-note"
                  onChange={(e) => setDraft({ ...draft, designation: e.target.value })}
                />
              </div>

              <div className="field">
                <label htmlFor="vc-email">Email</label>
                <input
                  id="vc-email"
                  type="email"
                  value={draft.email}
                  aria-invalid={emailIssue ? true : undefined}
                  aria-describedby={'vc-email-note' + (emailIssue ? ' vc-email-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                />
                {emailIssue && (
                  <p className="field-error" id="vc-email-issue" role="alert">
                    {emailIssue.detail}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="vc-mobile">Mobile</label>
                <input
                  id="vc-mobile"
                  type="text"
                  inputMode="tel"
                  placeholder="+919876543210"
                  value={draft.mobileE164}
                  aria-invalid={mobileIssue ? true : undefined}
                  aria-describedby={'vc-mobile-note' + (mobileIssue ? ' vc-mobile-issue' : '')}
                  onChange={(e) => setDraft({ ...draft, mobileE164: e.target.value })}
                />
                {mobileIssue && (
                  <p className="field-error" id="vc-mobile-issue" role="alert">
                    {mobileIssue.detail}
                  </p>
                )}
              </div>
            </div>

            <div className="commit-bar">
              <button
                type="submit"
                className="primary"
                disabled={saving}
                data-action="tprm.vendor,vendor-record.a08"
                data-action-also="tprm.vendor,vendor-record.a09"
              >
                {saving ? 'Saving' : editing.mode === 'new' ? 'Add contact' : 'Save changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null)
                  setIssues([])
                  setSaveError(null)
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
